/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { House, ShoppingBag, User, MapPin, CreditCard, Building, type LucideIcon } from 'lucide-react';

import type { Brand } from '@/lib/brand/brand';
import { routes } from '@/route-paths';

/**
 * WHAT THE HEADER USER MENU'S "YOUR ACCOUNT" SECTION LISTS — one row per brand.
 *
 * WHY THIS FILE EXISTS. `user-menu.tsx` held ONE unconditional list of six
 * `<Link>`s serving both brands, and one of them — `Account Details` →
 * `/account` — is a destination only BG's rail lists. After the rail became a
 * per-brand document (`components/account-navigation/nav-composition.ts`), a NM
 * shopper following that row landed on a page NM's own rail does not carry, so
 * no rail item read as active. Same root cause and same fix as the rail itself:
 * a typed `Record<Brand, …>` read through `useBrand()`, never an inline
 * `brand === 'bg'` branch.
 *
 * LIVE EVIDENCE for the one row that differs.
 * `.reference/live-shots/owner-2026-08-22/live-nm-account-flyout-signedin-440.jpeg`
 * is the signed-in NM header flyout. Its `Your Account` section holds exactly
 * four rows — `Overview`, `Order History`, `NM Credit Card`,
 * `My Style Preferences` — followed by a separate `Your Store` section, with
 * `Sign Out` in the flyout's top bar. **There is no account-details row.** That
 * agrees with NM's rail, where the overview IS the details page, and it is the
 * only reason this file forks rather than repointing: sending NM's
 * `Account Details` row at `/account/overview` would have satisfied "an item
 * reads as active" by rendering the same destination twice in one menu.
 *
 * WHAT THIS FILE DELIBERATELY DOES **NOT** DO, so the gap is recorded rather
 * than mistaken for parity. Live NM's four flyout rows are not our five. We
 * additionally render `Address Book`, `Payment Methods` and `Store Preferences`,
 * and we omit `NM Credit Card` and `My Style Preferences` — both of which
 * `docs/archetypes/account.md:163` DEFERRED, and neither of which has a route.
 * Bringing the flyout to live's exact four is an `[ours-extra]` question about
 * three working destinations, which is a different change from fixing one that
 * goes nowhere useful. Only the broken row was touched.
 *
 * NOT FORKED, and each for a stated reason:
 *   the `Your Lists` section   one row, `Wishlist`, reached by both brands
 *   the sign-out form          live NM's flyout carries `Sign Out` and live BG's
 *                              rail does too, so both brands want it here
 *   the guest arm              already brand-split one level up, in
 *                              `user-actions.tsx` via `GUEST_SIGN_IN`
 */
export interface UserMenuEntry {
    /**
     * Translation key, with the namespace it belongs to named beside it.
     *
     * The menu reads from two namespaces — the account rail's labels live under
     * `account`, while `addressBook` and `paymentMethods` are header-owned
     * strings — and that split predates this file. Carrying `ns` per row keeps
     * it explicit instead of re-deriving it from the key's shape.
     */
    key: string;
    ns: 'account' | 'header';
    /** Fallback copy, and documentation of what the key is expected to say. */
    defaultValue: string;
    /** Storefront-relative destination, from `route-paths.ts`. */
    path: string;
    icon: LucideIcon;
}

/** One brand's user menu. */
export interface UserMenuComposition {
    /** The `YOUR ACCOUNT` section's rows, in render order. */
    accountItems: readonly UserMenuEntry[];
}

const OVERVIEW: UserMenuEntry = {
    key: 'navigation.overview',
    ns: 'account',
    defaultValue: 'Overview',
    path: routes.accountOverview,
    icon: House,
};
const ORDER_HISTORY: UserMenuEntry = {
    key: 'navigation.orderHistory',
    ns: 'account',
    defaultValue: 'Order History',
    path: routes.accountOrders,
    icon: ShoppingBag,
};
const ADDRESS_BOOK: UserMenuEntry = {
    key: 'menu.addressBook',
    ns: 'header',
    defaultValue: 'Address Book',
    path: routes.accountAddresses,
    icon: MapPin,
};
const PAYMENT_METHODS: UserMenuEntry = {
    key: 'menu.paymentMethods',
    ns: 'header',
    defaultValue: 'Payment Methods',
    path: routes.accountPaymentMethods,
    icon: CreditCard,
};
const STORE_PREFERENCES: UserMenuEntry = {
    key: 'navigation.storePreferences',
    ns: 'account',
    defaultValue: 'Store Preferences',
    path: routes.accountStorePreferences,
    icon: Building,
};

/**
 * `Account Details` → `/account`. BG-ONLY, and the one row this file forks over.
 *
 * `/account` is BG's landing and the second row of BG's rail. On NM it is a page
 * the rail does not list; see the file header for the live capture that settles
 * it.
 */
const ACCOUNT_DETAILS: UserMenuEntry = {
    key: 'navigation.accountDetails',
    ns: 'account',
    defaultValue: 'Account Details',
    path: routes.account,
    icon: User,
};

/**
 * The user menu, per brand. Read through {@link userMenuFor}.
 */
export const USER_MENU_BY_BRAND: Record<Brand, UserMenuComposition> = {
    nm: {
        accountItems: [OVERVIEW, ORDER_HISTORY, ADDRESS_BOOK, PAYMENT_METHODS, STORE_PREFERENCES],
    },
    bg: {
        accountItems: [OVERVIEW, ACCOUNT_DETAILS, ORDER_HISTORY, ADDRESS_BOOK, PAYMENT_METHODS, STORE_PREFERENCES],
    },
};

/**
 * The user-menu document for one brand.
 *
 * A function rather than direct indexing at the call site, matching
 * `accountNavFor` — the two arms are reached through one place, and a consumer
 * cannot hold a reference to the wrong brand's document across a render.
 */
export function userMenuFor(brand: Brand): UserMenuComposition {
    return USER_MENU_BY_BRAND[brand];
}
